import { Service } from '@timing71/common';
import { Message, RaceControlMessage } from '@timing71/common/messages';
import { GPSAuge } from './gpsauge.js';
import { getManifest, translate } from './translate.js';

const TWO_MINUTES = 2 * 60 * 1000;

export class Wige extends Service {

  constructor(...args) {
    super(...args);
    this._socket = null;

    this._data = {};
    this._prevFirstMessage = null;
    this._timeData = {};

    this._handleMessage = this._handleMessage.bind(this);
    this._handleFlagZones = this._handleFlagZones.bind(this);

    this._gpsauge = null;
    this._prevFlagZones = {};
  }

  start(connectionService) {
    super.start(connectionService);

    const match = this.service.source.match(Wige.regex);
    const eventID = match.groups.eventID;

    this._socket = connectionService.createWebsocket(
      'wss://livetiming.azurewebsites.net/',
      {
        tag: this.service.uuid,
        connectionTimeout: TWO_MINUTES
      }
    );

    this._socket.on(
      'open',
      () => {
        this.emitInfo(null, 'Connected to upstream timing source');
        this._socket.send(
          JSON.stringify({
            "eventId": eventID,
            "eventPid":[0, 3, 4]
          })
        )
      }
    );

    this._socket.on(
      'close',
      () => {
        this.emitWarning(null, 'Disconnected from upstream timing source');
      }
    );

    this._socket.on(
      'message',
      (rawMsg) => {
        if (rawMsg.data) {
          this._handleMessage(
            JSON.parse(rawMsg.data)
          );
        }
        else {
          this._handleMessage(
            JSON.parse(rawMsg.toString())
          );
        }
      });

      if (eventID === '20') {
        // NLS
        this._gpsauge = new GPSAuge(connectionService, 'IPHNGR24');
        this._gpsauge.on('flagZones', this._handleFlagZones);
        this._gpsauge.start();
      }
      else if (eventID === '50') {
        // N24
        this._gpsauge = new GPSAuge(connectionService, 'IPHADAC24H');
        this._gpsauge.on('flagZones', this._handleFlagZones);
        this._gpsauge.start();
      }

  }

  _handleMessage(message) {

    let timingUpdate = false;
    let manifestUpdate = false;

    switch(message.PID) {

      case '0': // Timing data

        manifestUpdate = message.CUP !== this._data.CUP ||
          message.HEAT !== this._data.HEAT ||
          message.NROFINTERMEDIATETIMES !== this._data.NROFINTERMEDIATETIMES;

        if (this._data.SESSION && message.SESSION !== this._data.SESSION) {
          this.onSessionChange();
        }

        this._data = message;
        timingUpdate = true;
        break;

      case '3': // Messages (rarely seen in the wild)
        this._handleRCMessages(message.MESSAGES || []);
        break;

      case '4': // Session time
        this._timeData = { ...this._timeData,  ...message };
        timingUpdate = true;
        break;

      case 'LTS_TIMESYNC':
        // Try to work out offset between server time and UTC
        const serverTime = parseInt(message.serverLocalTime, 10);
        const utcTime = Date.now();
        const offset = Math.round((serverTime - utcTime) / 1000 / 60 / 60);
        this._timeData.calculatedOffset = offset;
        break;

      default:
        console.warn(`Message received with unhandled PID:`, message);

    }

    if (manifestUpdate) {
      this.onManifestChange(getManifest(this._data, !!this._gpsauge));
    }

    if (timingUpdate) {
      this.onStateChange(translate(this._data, this._messages, this._timeData, this._prevFlagZones));
    }
  }

  _handleRCMessages(allMessages) {
    let newMessages = [];

    if (!this._prevFirstMessage) {
      // Don't immediately report all previous messages on startup!
      newMessages = [];
    }
    else {
      for (const message of allMessages) {
        if (message.MESSAGETIME === this._prevFirstMessage.MESSAGETIME && message.MESSAGE === this._prevFirstMessage.MESSAGE) {
          break;
        }
        newMessages.unshift(message);
      }
    }

    if (newMessages.length > 0) {
      const ctdMessages = newMessages.map(
        // I don't trust the timestamp included in the message - it has no date
        // nor timezone so we can't easily use it
        msg => new RaceControlMessage(msg.MESSAGE.toUpperCase()).toCTDFormat()
      );
      this.onStateChange({ extraMessages: ctdMessages });
    }

    this._prevFirstMessage = allMessages[0];
  }

  _handleFlagZones(zones) {
    const extraMessages = [];

    Object.entries(zones).forEach(
      ([zoneID, zone]) => {
        if (this._prevFlagZones[zoneID] && [60, 120].includes(this._prevFlagZones[zoneID].zoneType)) {
          const prev = this._prevFlagZones[zoneID];
          if (prev.zoneType === 60 && zone.zoneType === 120) {
            extraMessages.push(
              new Message('Track', `Code 60 downgraded to slow zone at MP${zone.post} (${zone.name})`, 'yellow').toCTDFormat()
            );
          }
          else if (prev.zoneType === 120 && zone.zoneType === 60) {
            extraMessages.push(
              new Message('Track', `Slow zone upgraded to code 60 at MP${zone.post} (${zone.name})`, 'code60').toCTDFormat()
            );
          }
        }
        else {
          if (this._prevFlagZones[zoneID]?.zoneType !== 0 && zone.zoneType === 0) {
            extraMessages.push(
              new Message('Track', `Yellow flag at MP${zone.post} (${zone.name})`, 'yellow').toCTDFormat()
            );
          }
          else if (zone.zoneType === 120) {
            extraMessages.push(
              new Message('Track', `New slow zone at MP${zone.post} (${zone.name})`, 'yellow').toCTDFormat()
            );
          }
          else if (zone.zoneType === 60) {
            extraMessages.push(
              new Message('Track', `New Code 60 zone at MP${zone.post} (${zone.name})`, 'code60').toCTDFormat()
            );
          }
        }
      }
    );

    Object.entries(this._prevFlagZones).forEach(
      ([zoneID, zone]) => {
        if (!zones[zoneID] || (zones[zoneID]?.zoneType < 0 && zone.zoneType >= 0)) {
          extraMessages.push(
            new Message('Track', `Track clear at MP${zone.post} (${zone.name})`, 'green').toCTDFormat()
          );
        }
      }
    )

    this._prevFlagZones = zones;
    const newState = translate(this._data, this._messages, this._timeData, this._prevFlagZones);
    this.onStateChange({ ...newState, extraMessages });
  }

  stop() {
    this._gpsauge?.stop();
    this._socket?.close();
  }

  getTransientStateForSaving() {
    return {
      data: this._data,
      prevFirstMessage: this._prevFirstMessage,
      prevFlagZones: this._prevFlagZones,
      timeData: this._timeData
    };
  }

  restoreTransientState(state) {
    this._data = state.data || {};
    this._prevFirstMessage = state._prevFirstMessage || null;
    this._prevFlagZones = state.prevFlagZones || {};
    this._timeData = state.timeData || {};
  }

}

Wige.regex = /livetiming.azurewebsites.net\/event(=|s\/)(?<eventID>[0-9]+)/;
